import { SimulationConfig } from '../../types';
import { BALL_VERTEX_SHADER, BALL_FRAGMENT_SHADER } from '../../shaders';
import { WebGLContext, MAX_BALLS } from './WebGLContext';
import { WebGLUtils } from './WebGLUtils';

export class FXRenderer {
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private posBuffer: WebGLBuffer;
  private radiusBuffer: WebGLBuffer;
  private colorBuffer: WebGLBuffer;
  private alphaBuffer: WebGLBuffer;
  private posData: Float32Array;
  private radiusData: Float32Array;
  private colorData: Float32Array;
  private alphaData: Float32Array;

  constructor(private context: WebGLContext) {
    const gl = context.gl;
    this.program = WebGLUtils.createProgram(gl, BALL_VERTEX_SHADER, BALL_FRAGMENT_SHADER);
    this.posData = new Float32Array(MAX_BALLS * 2);
    this.radiusData = new Float32Array(MAX_BALLS);
    this.colorData = new Float32Array(MAX_BALLS * 3);
    this.alphaData = new Float32Array(MAX_BALLS);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    gl.bindBuffer(gl.ARRAY_BUFFER, context.geoBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.posBuffer = context.createAttribBuffer(1, 2, this.posData.byteLength);
    this.radiusBuffer = context.createAttribBuffer(2, 1, this.radiusData.byteLength);
    this.colorBuffer = context.createAttribBuffer(3, 3, this.colorData.byteLength);
    this.alphaBuffer = context.createAttribBuffer(4, 1, this.alphaData.byteLength);

    gl.bindVertexArray(null);
  }

  render(events: any[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) {
      if (events.length === 0) return;
      const gl = this.context.gl;
      gl.useProgram(this.program);
      gl.bindVertexArray(this.vao);

      WebGLUtils.updateCommonUniforms(gl, this.program, this.context.width, this.context.height, camera, zoomLevel);
      
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_pulseSpeed'), 0);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_squashFactor'), 0);
      gl.uniform1i(gl.getUniformLocation(this.program, 'u_ballStyle'), 0);
      gl.uniform1i(gl.getUniformLocation(this.program, 'u_textureType'), 0);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_textureOpacity'), 0);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_glowAmount'), 0);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_fillOpacity'), 1.0);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_strokeOpacity'), 0);
      gl.uniform1f(gl.getUniformLocation(this.program, 'i_borderWidth'), 0);

      const count = Math.min(events.length, MAX_BALLS);
      const now = Date.now();

      for (let i = 0; i < count; i++) {
          const e = events[i];
          const elapsed = now - e.startTime;
          const progress = Math.min(1.0, elapsed / e.duration);
          
          this.posData[i * 2] = e.pos.x;
          this.posData[i * 2 + 1] = e.pos.y;
          this.radiusData[i] = e.radius * (1.0 + progress);
          WebGLUtils.parseColor(e.color, this.colorData, i * 3);
          this.alphaData[i] = 1.0 - progress;
      }

      this.uploadData(count);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
      gl.bindVertexArray(null);
  }

  private uploadData(count: number) {
    const gl = this.context.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.posData.subarray(0, count * 2));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.radiusBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.radiusData.subarray(0, count));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.colorData.subarray(0, count * 3));
    gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.alphaData.subarray(0, count));
  }
}
